// Custom ReactFlow node for trigger blocks (orange)
import React, { memo } from 'react';
import { Handle, Position } from 'reactflow';
import { Box, Typography, Chip } from '@mui/material';
import {
  PlayCircle as TriggerIcon,
  Http as WebhookIcon
} from '@mui/icons-material';

const TriggerNode = memo(({ data, selected }) => {
  const isWebhook = data.blockType === 'api_webhook';
  const Icon = isWebhook ? WebhookIcon : TriggerIcon;
  const debugStep = data._debugStep;

  return (
    <Box sx={{
      minWidth: 180,
      border: selected ? '2px solid #e65100' : '1px solid #ef6c00',
      borderRadius: 1,
      bgcolor: 'background.paper',
      boxShadow: selected ? 3 : 1,
      ...(debugStep && {
        boxShadow: debugStep.status === 'completed'
          ? '0 0 8px rgba(46,125,50,0.4)' : debugStep.status === 'failed'
            ? '0 0 8px rgba(198,40,40,0.4)' : 1,
      }),
    }}>
      {/* Header */}
      <Box sx={{
        px: 1.5, py: 0.75,
        bgcolor: '#ef6c00',
        color: 'white',
        borderRadius: '3px 3px 0 0',
        display: 'flex',
        alignItems: 'center',
        gap: 0.75,
      }}>
        <Icon sx={{ fontSize: 16 }} />
        <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '0.65rem' }}>
          Trigger
        </Typography>
        {debugStep && (
          <Chip
            label={debugStep.status === 'completed' ? 'OK' : 'ERR'}
            size="small"
            sx={{
              ml: 'auto',
              height: 16,
              fontSize: '0.55rem',
              fontWeight: 700,
              bgcolor: debugStep.status === 'completed' ? 'rgba(255,255,255,0.25)' : 'rgba(198,40,40,0.7)',
              color: 'white',
            }}
          />
        )}
      </Box>

      {/* Body */}
      <Box sx={{ px: 1.5, py: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
          {data.label || 'Output State Changed'}
        </Typography>
        {data.summary && (
          <Typography variant="caption" color="text.secondary">
            {data.summary}
          </Typography>
        )}
        {!data.summary && data.config?.outputName && (
          <Typography variant="caption" color="text.secondary">
            {data.config.outputName}
          </Typography>
        )}
        {!data.summary && !data.config?.outputName && data.config?.outputId && (
          <Typography variant="caption" color="text.secondary">
            Output #{data.config.outputId}
          </Typography>
        )}
        {isWebhook && data.config?.endpointPath && (
          <Typography variant="caption" color="text.secondary" sx={{
            display: 'block',
            fontFamily: 'monospace',
            fontSize: '0.65rem',
            mt: 0.25,
          }}>
            /api/automations/{data.config.endpointPath}
          </Typography>
        )}

        {/* Debug data indicator */}
        {debugStep?.output && (
          <Typography variant="caption" sx={{
            display: 'block',
            fontFamily: 'monospace',
            fontSize: '0.6rem',
            mt: 0.5,
            color: '#e65100',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: 160,
          }}>
            {typeof debugStep.output === 'object'
              ? JSON.stringify(debugStep.output).substring(0, 40) + '...'
              : String(debugStep.output).substring(0, 40)}
          </Typography>
        )}
      </Box>

      {/* Output handle */}
      <Handle
        type="source"
        position={Position.Bottom}
        style={{ background: '#ef6c00', width: 10, height: 10 }}
      />
    </Box>
  );
});

TriggerNode.displayName = 'TriggerNode';
export default TriggerNode;
